import { useState } from 'react';
import { Text, Spinner, makeStyles, tokens, Badge } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { fetchHarvestSummary } from '../api/harvest.api';
import { useEntityNames } from '../hooks/useEntityNames';
import { fetchMermaSummary, type MermaReason } from '../api/merma.api';
import { fetchModules, type ModuleStatus } from '../api/terrain.api';
import { fetchForecasts, FORECAST_HORIZONS, type ForecastHorizon, type Forecast } from '../api/production.api';
import { KpiCard } from '../components/shared/KpiCard';
import { DataTable, type DataTableColumn } from '../components/shared/DataTable';
import { ParcelStatusGrid, type ParcelStatusCell } from '../components/shared/ParcelStatusGrid';
import { DonutChart, type DonutChartSlice } from '../components/shared/charts/DonutChart';
import { SubTabs } from '../components/shared/SubTabs';
import { useProjectStore } from '../store/project.store';
import { useT } from '../i18n';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { useRanchoStore } from '../store/rancho.store';
import { PageHeader } from '../components/shared/PageHeader';

/**
 * F7 — "Rendimiento y Cosecha" del portal inversionista. Solo lectura,
 * reusa `fetchHarvestSummary`/`fetchMermaSummary` (mismas fuentes que
 * HarvestPage/MermaPage) — no depende de F2 (forecast plan-vs-real, aún
 * sin construir). Los grados A/B/C se muestran con la etiqueta A/B/C real
 * del PDF pendiente de definir presentación — por ahora igual a HarvestPage.
 */

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  kpiRow:  { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  sectionTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  reasonRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '8px 14px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 12px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  td: { padding: '8px 12px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200 },
});

const MERMA_REASONS: MermaReason[] = ['CLIMA', 'PLAGA', 'MECANICO', 'RECHAZO_CALIDAD', 'PUDRICION', 'OTRO'];

/** Mismo criterio semáforo que `InvestorOverviewPage`/`RiesgosPage` (STATUS_FILL) —
 * usado aquí como proxy de avance de cosecha: no existe todavía un % de
 * progreso de cosecha por módulo, así que se reutiliza el status operativo
 * (F1), donde 'COSECHANDO' es el valor que más interesa a este tab. */
const STATUS_FILL: Record<ModuleStatus, string> = {
  PREPARACION: '#8a8886',
  NORMAL:      '#107c10',
  ATENCION:    '#ca5010',
  CRITICO:     '#d83b01',
  COSECHANDO:  '#0078d4',
};

const REASON_COLOR: Record<MermaReason, string> = {
  CLIMA:            '#0078d4',
  PLAGA:            '#d83b01',
  MECANICO:         '#8764b8',
  RECHAZO_CALIDAD:  '#ca5010',
  PUDRICION:        '#a4262c',
  OTRO:             '#8a8886',
};

interface ModuleScenarioRow {
  moduleId: string;
  moduleCode: string;
  cropName: string;
  byHorizon: Partial<Record<ForecastHorizon, Forecast | undefined>>;
}

interface ForecastTotals {
  conservador: number;
  base: number;
  ideal: number;
  unit: string;
}

/** Suma el escenario a 15 días de todos los módulos del proyecto — "producción
 * prevista" agregada, complemento de la tabla comparativa por módulo. */
function sumScenarios15d(rows: ModuleScenarioRow[]): ForecastTotals {
  return rows.reduce<ForecastTotals>((acc, r) => {
    const f = r.byHorizon[15];
    if (!f) return acc;
    return {
      conservador: acc.conservador + f.scenarios.conservador,
      base: acc.base + f.scenarios.base,
      ideal: acc.ideal + f.scenarios.ideal,
      unit: f.unit,
    };
  }, { conservador: 0, base: 0, ideal: 0, unit: 'kg' });
}

export function InvestorYieldPage() {
  const s = useStyles();
  const t = useT();
  const { fmtNum } = useIntlFormat();
  const { formatCurrency, formatDate } = useLocaleSettings();
  const { loteName } = useEntityNames();
  const activeProjectId = useProjectStore(st => st.activeProjectId);
  const activeRanchoId = useRanchoStore(st => st.activeRanchoId);
  const [subtab, setSubtab] = useState<'resumen' | 'analisis'>('resumen');

  const { data: harvest, isLoading: harvestLoading } = useQuery({
    queryKey: ['investor-harvest-summary', activeRanchoId],
    queryFn: () => fetchHarvestSummary(activeRanchoId),
  });
  const { data: merma, isLoading: mermaLoading } = useQuery({
    queryKey: ['investor-merma-summary', activeRanchoId],
    queryFn: () => fetchMermaSummary(activeRanchoId),
  });

  // Rendimiento-Análisis (Fase C) — escenarios de forecast por módulo del
  // proyecto activo, comparados lado a lado. Reusa `fetchModules`/`fetchForecasts`
  // (ya usados en InvestorProductionPage), sin backend nuevo.
  const { data: modules = [] } = useQuery({
    queryKey: ['yield-analysis-modules', activeProjectId, activeRanchoId],
    queryFn: () => fetchModules({ projectId: activeProjectId!, ranchoId: activeRanchoId }),
    enabled: !!activeProjectId && subtab === 'analisis',
  });

  const { data: scenarioRows = [], isLoading: scenariosLoading } = useQuery({
    queryKey: ['yield-analysis-scenarios', modules.map(m => m.moduleId).join(',')],
    queryFn: async (): Promise<ModuleScenarioRow[]> => Promise.all(modules.map(async m => {
      const byHorizon: Partial<Record<ForecastHorizon, Forecast | undefined>> = {};
      await Promise.all(FORECAST_HORIZONS.map(async h => {
        const items = await fetchForecasts(m.moduleId, { horizonDays: h, limit: 1 });
        byHorizon[h] = items[0];
      }));
      return { moduleId: m.moduleId, moduleCode: m.moduleCode, cropName: m.cropName ?? '—', byHorizon };
    })),
    enabled: modules.length > 0 && subtab === 'analisis',
  });

  const forecastTotals = sumScenarios15d(scenarioRows);

  const reasonEntries = merma
    ? MERMA_REASONS.map(r => [r, merma.kpi.rejectedByReason[r] ?? 0] as const).filter(([, v]) => v > 0)
    : [];
  const reasonTotal = reasonEntries.reduce((sum, [, v]) => sum + v, 0);
  const lossDonutData: DonutChartSlice[] = reasonEntries.map(([reason, value]) => ({
    label: t.merma[`reason${reason}` as keyof typeof t.merma] as string,
    color: REASON_COLOR[reason],
    pct: reasonTotal > 0 ? Math.round((value / reasonTotal) * 1000) / 10 : 0,
  }));

  const totalKg    = harvest?.kpis.reduce((sum, k) => sum + (k.totalKg ?? 0), 0) ?? 0;
  const avgPackout = harvest?.kpis.length
    ? harvest.kpis.reduce((sum, k) => sum + (k.avgPackout ?? 0) * (k.totalKg ?? 0), 0) / (totalKg || 1)
    : 0;
  // Los grados salen de `kpis` (agregado de todo el tenant), no de `records`
  // (una muestra acotada del historial): sumar la muestra subestimaría el total,
  // y mientras `records` vino vacío las tres tarjetas mostraban 0 con kg reales.
  const totalGradeA = harvest?.kpis.reduce((sum, k) => sum + (k.gradeA ?? 0), 0) ?? 0;
  const totalGradeB = harvest?.kpis.reduce((sum, k) => sum + (k.gradeB ?? 0), 0) ?? 0;
  const totalGradeC = harvest?.kpis.reduce((sum, k) => sum + (k.gradeC ?? 0), 0) ?? 0;

  return (
    <div className={s.root}>
      <PageHeader title={t.investorYield.title} subtitle={t.investorYield.subtitle} />

      <SubTabs
        selected={subtab}
        onSelect={v => setSubtab(v as 'resumen' | 'analisis')}
        tabs={[
          { value: 'resumen',  label: t.investorYield.tabResumen },
          { value: 'analisis', label: t.investorYield.tabAnalisis },
        ]}
      />

      {subtab === 'analisis' && (
        <div className={s.section}>
          <span className={s.sectionTitle}>{t.investorYield.analysisTitle}</span>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.investorYield.analysisSubtitle}</Text>
          {!activeProjectId && <Text size={200}>{t.investorYield.analysisNoProject}</Text>}
          {activeProjectId && (
            <>
              <span className={s.sectionTitle}>{t.investorYield.analysisForecastTitle}</span>
              <div className={s.kpiRow}>
                <KpiCard label={t.investorProduction.scenarioConservador} value={`${forecastTotals.conservador.toFixed(0)} ${forecastTotals.unit}`} />
                <KpiCard label={t.investorProduction.scenarioBase} value={`${forecastTotals.base.toFixed(0)} ${forecastTotals.unit}`} />
                <KpiCard label={t.investorProduction.scenarioIdeal} value={`${forecastTotals.ideal.toFixed(0)} ${forecastTotals.unit}`} />
              </div>
            </>
          )}
          <span className={s.sectionTitle}>{t.investorYield.harvestMapTitle}</span>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.investorYield.harvestMapSubtitle}</Text>
          {modules && modules.length > 0 && (
            <ParcelStatusGrid
              cells={modules.map((m): ParcelStatusCell => ({ key: m.moduleId, label: m.moduleCode, color: STATUS_FILL[m.status] }))}
              legend={(Object.keys(STATUS_FILL) as ModuleStatus[]).map(st => ({
                label: (t.investorModules as any)[`status${st}`] || st,
                color: STATUS_FILL[st],
              }))}
            />
          )}
          <span className={s.sectionTitle}>{t.investorYield.lossReasonsTitle}</span>
          {lossDonutData.length === 0 && <Text size={200}>{t.merma.noRecords}</Text>}
          {lossDonutData.length > 0 && (
            <DonutChart data={lossDonutData} centerValue={`${reasonTotal.toFixed(0)} kg`} centerLabel={t.investorYield.lossReasonsTitle} />
          )}
          {activeProjectId && (
            <DataTable<ModuleScenarioRow>
              isLoading={scenariosLoading}
              emptyMessage={t.investorYield.analysisNoModules}
              rows={scenarioRows}
              rowKey={r => r.moduleId}
              columns={[
                { key: 'moduleCode', header: t.investorYield.analysisColModule },
                { key: 'cropName', header: t.investorYield.analysisColCrop },
                ...FORECAST_HORIZONS.map((h): DataTableColumn<ModuleScenarioRow> => ({
                  key: `h${h}`,
                  header: `${h}d`,
                  align: 'right',
                  render: r => {
                    const f = r.byHorizon[h];
                    if (!f) return '—';
                    return `${f.scenarios.conservador.toFixed(0)} / ${f.scenarios.base.toFixed(0)} / ${f.scenarios.ideal.toFixed(0)} ${f.unit}`;
                  },
                })),
              ]}
            />
          )}
          <Text size={100} style={{ color: tokens.colorNeutralForeground3 }}>{t.investorYield.analysisScenarioLegend}</Text>
        </div>
      )}

      {subtab === 'resumen' && <>
      {/* Cosecha */}
      <div className={s.section}>
        <span className={s.sectionTitle}>{t.investorYield.harvestTitle}</span>
        {harvestLoading && <Spinner size="small" label={t.harvest.loadingHarvests} />}
        {harvest && (
          <>
            <div className={s.kpiRow}>
              <KpiCard label={t.harvest.kpiTotalKg} value={fmtNum(totalKg)} />
              <KpiCard label={t.harvest.kpiPackout} value={`${avgPackout.toFixed(1)}%`} sub={t.harvest.kpiPackoutSub} />
              <KpiCard label={t.harvest.kpiCycles} value={fmtNum(harvest.records.length)} />
            </div>
            <div className={s.kpiRow}>
              <KpiCard label={t.harvest.chartGradeA} value={fmtNum(totalGradeA)} />
              <KpiCard label={t.harvest.chartGradeB} value={fmtNum(totalGradeB)} />
              <KpiCard label={t.harvest.chartGradeC} value={fmtNum(totalGradeC)} accent={totalGradeC > 0} />
            </div>
          </>
        )}
      </div>

      {/* Merma */}
      <div className={s.section}>
        <span className={s.sectionTitle}>{t.investorYield.mermaTitle}</span>
        {mermaLoading && <Spinner size="small" label={t.merma.loading} />}
        {merma && (
          <>
            <div className={s.kpiRow}>
              <KpiCard label={t.merma.kpiRejectedKg} value={`${(merma.kpi.totalRejectedKg ?? 0).toFixed(1)} kg`} accent={(merma.kpi.totalRejectedKg ?? 0) > 0} />
              <KpiCard label={t.merma.kpiImpact} value={formatCurrency(merma.kpi.totalWasteImpactCost ?? 0)} />
              <KpiCard label={t.merma.kpiRecords} value={String(merma.kpi.recordCount ?? 0)} />
            </div>
            <div className={s.section}>
              {MERMA_REASONS.filter(r => (merma.kpi.rejectedByReason[r] ?? 0) > 0).map(r => (
                <div key={r} className={s.reasonRow}>
                  <Text size={200}>{t.merma[`reason${r}` as keyof typeof t.merma]}</Text>
                  <Badge appearance="tint">{(merma.kpi.rejectedByReason[r] ?? 0).toFixed(1)} kg</Badge>
                </div>
              ))}
              {merma.kpi.recordCount === 0 && <Text size={200}>{t.merma.noRecords}</Text>}
            </div>
          </>
        )}
      </div>

      {/* Historial reciente de cosecha */}
      <div className={s.section}>
        <span className={s.sectionTitle}>{t.harvest.tableTitle}</span>
        {harvest && harvest.records.length > 0 ? (
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>{t.harvest.tableDate}</th>
                <th className={s.th}>{t.harvest.tableLot}</th>
                <th className={s.th} style={{ textAlign: 'right' }}>{t.harvest.tableKg}</th>
                <th className={s.th} style={{ textAlign: 'right' }}>{t.harvest.tablePackout}</th>
              </tr>
            </thead>
            <tbody>
              {harvest.records.slice(0, 10).map(r => (
                <tr key={r.harvestId}>
                  <td className={s.td}>{formatDate(r.harvestedAt.slice(0, 10))}</td>
                  <td className={s.td}>{loteName(r.loteId)}</td>
                  <td className={s.td} style={{ textAlign: 'right' }}>{fmtNum(r.kgTotal)}</td>
                  <td className={s.td} style={{ textAlign: 'right' }}>{r.packoutPct != null ? `${r.packoutPct.toFixed(1)}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          !harvestLoading && <Text size={200}>{t.harvest.noHarvests}</Text>
        )}
      </div>
      </>}
    </div>
  );
}
